import React, { useState, useEffect } from 'react';
import WatchlistAssignGroupsModal from './WatchlistAssignGroupsModal';
import WatchlistContractCard from './WatchlistContractCard';
import apiClient, {
  refreshGroupContracts,
  getJobStatus,
} from '../services/api';
import { isUsageLimitError, getUsageLimitData } from '../utils/usageLimitHelper';
import UsageLimitModal from './UsageLimitModal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const COLLAPSED_GROUPS_STORAGE_KEY = 'watchlist_collapsed_groups';

function WatchlistGroups({
  groups,
  onRunGroup,
  fetchGroups,
  selectedContracts,
  setSelectedContracts,
  handleRunSelected,
  handleSimulationStart,
  handleSimulationComplete,
  handleBulkDelete,
  handleBulkAssign,
  baselineMode,
  setBaselineMode,
}) {
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [contractsForAssignment, setContractsForAssignment] = useState([]);
  const [contractDetailsForAssignment, setContractDetailsForAssignment] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [refreshingGroups, setRefreshingGroups] = useState({});
  const [refreshJobs, setRefreshJobs] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({}); // Track which groups show all contracts
  const [editingGroupId, setEditingGroupId] = useState(null); // Track which group is being edited
  const [editingGroupName, setEditingGroupName] = useState(''); // New name being typed
  const [usageLimitData, setUsageLimitData] = useState(null);

  // --- Toggle Collapse ---
  const toggleCollapse = (groupId) => {
    console.group(`📂 Toggling Group Collapse - Group ID: ${groupId}`);
    console.log('Before:', collapsedGroups);
    setCollapsedGroups((prev) => {
      const updated = { ...prev, [groupId]: !prev[groupId] };
      console.log('After:', updated);
      console.groupEnd();
      return updated;
    });
  };

  // Persist collapsed groups to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_GROUPS_STORAGE_KEY, JSON.stringify(collapsedGroups));
    } catch {
      // Silently fail if localStorage unavailable
    }
  }, [collapsedGroups]);

  // --- Toggle Show All Contracts ---
  const toggleShowAll = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // --- Open Assign Modal ---
  const openAssignModalForContracts = (contractIds) => {
    console.group('🔗 Opening Assign Modal');
    console.log('Contracts to assign:', contractIds);

    // Look up contract details from groups
    const allContracts = groups.flatMap(g => g.contracts);
    const details = contractIds.map(id => {
      const contract = allContracts.find(c => c.id === id);
      if (contract) {
        return {
          ticker: contract.ticker,
          strike: contract.strike,
          expiration: contract.expiration,
          numberOfContracts: contract.number_of_contracts || 1
        };
      }
      return null;
    }).filter(Boolean);

    console.log('Contract details found:', details);
    console.groupEnd();

    setContractsForAssignment(contractIds);
    setContractDetailsForAssignment(details);
    setAssignModalVisible(true);
  };

  // --- Reset Days ---
  const handleResetDays = async (contractId) => {
    console.group(`⏳ Resetting Countdown - Contract ID: ${contractId}`);
    try {
      await apiClient.patch(`/saved-contracts/${contractId}/reset-days/`);
      console.log(`✅ Countdown reset for contract ID: ${contractId}`);
      await fetchGroups();
    } catch (err) {
      console.error(`❌ Error resetting countdown for contract ID ${contractId}:`, err);
      alert("Failed to reset countdown.");
    }
    console.groupEnd();
  };

  // --- Refresh Contract ---
  const handleRefresh = async (contractId) => {
    console.group(`🔄 Refreshing Contract - Contract ID: ${contractId}`);
    try {
      await apiClient.patch(`/saved-contracts/${contractId}/refresh/`);
      console.log(`✅ Data refreshed for contract ID: ${contractId}`);
      await fetchGroups();
    } catch (err) {
      console.error(`❌ Error refreshing contract ID ${contractId}:`, err);
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else {
        alert("Failed to refresh contract data.");
      }
    }
    console.groupEnd();
  };

  // --- Delete Contract ---
  const handleDeleteContract = async (contractId, groupId) => {
    console.group(`🗑 Deleting Contract - Contract ID: ${contractId}, Group ID: ${groupId}`);
    try {
      await apiClient.delete(`/watchlist-groups/${groupId}/contracts/${contractId}/`);
      console.log(`✅ Successfully removed contract ID: ${contractId} from group ID: ${groupId}`);
      await fetchGroups();
    } catch (err) {
      console.error(`❌ Error removing contract ${contractId} from group ${groupId}:`, err);
      alert("Failed to remove contract from group.");
    }
    console.groupEnd();
  };

  // --- Delete Group ---
  const handleDeleteGroup = async (groupId, groupName, contractCount) => {
    console.group(`🗑 Deleting Group - Group ID: ${groupId}`);

    // Confirmation dialog
    const confirmMessage = contractCount > 0
      ? `Delete group "${groupName}" with ${contractCount} contract(s)?\n\nNote: Contracts will NOT be deleted, only removed from this group.`
      : `Delete empty group "${groupName}"?`;

    if (!window.confirm(confirmMessage)) {
      console.log('❌ Group deletion cancelled by user');
      console.groupEnd();
      return;
    }

    try {
      await apiClient.delete(`/watchlist-groups/${groupId}/`);
      console.log(`✅ Successfully deleted group ID: ${groupId}`);
      await fetchGroups();
      alert(`Group "${groupName}" deleted successfully!`);
    } catch (err) {
      console.error(`❌ Error deleting group ${groupId}:`, err);
      alert(`Failed to delete group: ${err.response?.data?.error || err.message}`);
    }
    console.groupEnd();
  };

  // --- Start Editing Group Name ---
  const handleStartEditGroup = (groupId, currentName) => {
    console.log(`✏️ Starting edit for group ${groupId}: "${currentName}"`);
    setEditingGroupId(groupId);
    setEditingGroupName(currentName);
  };

  // --- Cancel Editing ---
  const handleCancelEdit = () => {
    console.log('❌ Cancelled editing');
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  // --- Save Group Name ---
  const handleSaveGroupName = async (groupId) => {
    console.group(`💾 Saving Group Name - Group ID: ${groupId}`);

    // Validate name
    if (!editingGroupName.trim()) {
      alert('Group name cannot be empty');
      console.groupEnd();
      return;
    }

    try {
      await apiClient.put(`/watchlist-groups/${groupId}/`, {
        name: editingGroupName.trim()
      });
      console.log(`✅ Successfully updated group name to: "${editingGroupName}"`);
      setEditingGroupId(null);
      setEditingGroupName('');
      await fetchGroups();
    } catch (err) {
      console.error(`❌ Error updating group name:`, err);
      alert(`Failed to update group name: ${err.response?.data?.error || err.message}`);
    }
    console.groupEnd();
  };

  // --- Run Group Simulation ---
  const handleRunGroup = async (groupId) => {
    console.group(`▶ Run Group Simulation - Group ID: ${groupId}`);
    try {
      console.log('Triggering onRunGroup with Group ID:', groupId);
      onRunGroup(groupId);
    } catch (err) {
      console.error(`❌ Error running group simulation for group ID ${groupId}:`, err);
      alert("Failed to run group simulation.");
    }
    console.groupEnd();
  };

  // --- Poll Job Status ---
  const pollJobStatus = async (jobId, groupId) => {
    try {
      const response = await getJobStatus(jobId);
      const job = response.data;

      setRefreshJobs(prev => ({
        ...prev,
        [groupId]: job
      }));

      if (job.status === 'completed') {
        setRefreshingGroups(prev => ({
          ...prev,
          [groupId]: false
        }));

        // Only show alerts for partial failures
        if (job.failed_contracts > 0) {
          alert(`❌ Refresh completed: ${job.successful_contracts} contracts updated, ${job.failed_contracts} failed.`);
        }
        // Silent success - no alert for fully successful refreshes

        // Refresh the groups data
        await fetchGroups();

      } else if (job.status === 'failed') {
        setRefreshingGroups(prev => ({
          ...prev,
          [groupId]: false
        }));
        alert(`❌ Refresh failed: ${job.error_message || 'Unknown error'}`);

      } else {
        // Still running, poll again after 2 seconds
        setTimeout(() => pollJobStatus(jobId, groupId), 2000);
      }
    } catch (err) {
      console.error(`Error polling job status:`, err);
      setRefreshingGroups(prev => ({
        ...prev,
        [groupId]: false
      }));
    }
  };

  // --- Refresh Group Contracts ---
  const handleRefreshGroup = async (groupId) => {
    console.group(`🔄 Refresh Group - Group ID: ${groupId}`);
    try {
      setRefreshingGroups(prev => ({
        ...prev,
        [groupId]: true
      }));

      const response = await refreshGroupContracts(groupId);
      const { job_id, message } = response.data;

      console.log(`✅ Started refresh job: ${message}`);

      // Start polling for job status
      pollJobStatus(job_id, groupId);

    } catch (err) {
      console.error(`❌ Error starting group refresh for group ID ${groupId}:`, err);
      setRefreshingGroups(prev => ({
        ...prev,
        [groupId]: false
      }));
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else {
        alert("Failed to start group refresh.");
      }
    }
    console.groupEnd();
  };

  return (
    <div className="mt-8">
      <h3 className="heading-lg">Watchlist Groups</h3>

      <div className="space-y-4">
        {groups.map((group) => {
          const isCollapsed = collapsedGroups[group.id];
          console.group(`📦 Rendering Group - ID: ${group.id}`);
          console.log('Name:', group.name);
          console.log('Collapsed State:', isCollapsed);
          console.log('Contracts Count:', group.contracts.length);
          console.groupEnd();

          const isRefreshing = refreshingGroups[group.id];
          const refreshJob = refreshJobs[group.id];

          return (
            <div key={group.id} className="card card-hover">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                {editingGroupId === group.id ? (
                  // Edit mode - show input and save/cancel buttons
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-brand-blue text-xl leading-none">
                      {isCollapsed ? '+' : '−'}
                    </span>
                    <input
                      type="text"
                      value={editingGroupName}
                      onChange={(e) => setEditingGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveGroupName(group.id);
                        } else if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                      className="bg-gray-800 border border-brand-gray-blue text-brand-white flex-1 text-lg font-bold px-3 py-2 rounded focus:outline-none focus:border-brand-blue"
                      autoFocus
                      placeholder="Enter group name"
                    />
                    <button
                      onClick={() => handleSaveGroupName(group.id)}
                      className="btn-action-accent text-xs"
                      title="Save (Enter)"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="btn-utility text-xs"
                      title="Cancel (Esc)"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  // Normal mode - show group name with inline edit button
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <button
                      onClick={() => toggleCollapse(group.id)}
                      className="flex items-center gap-2 text-brand-blue hover:text-brand-white font-bold text-lg transition-colors duration-200"
                    >
                      <span className="text-brand-blue text-xl leading-none">
                        {isCollapsed ? '+' : '−'}
                      </span>
                      {group.name} ({group.contracts.length} contracts)
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEditGroup(group.id, group.name);
                      }}
                      disabled={isRefreshing || editingGroupId !== null}
                      className={`text-brand-gray-blue hover:text-brand-white transition-colors p-1 ${
                        isRefreshing || editingGroupId !== null ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Edit group name"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleRefreshGroup(group.id)}
                    disabled={isRefreshing || group.contracts.length === 0}
                    className={`btn-secondary text-xs ${
                      isRefreshing || group.contracts.length === 0
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    data-tooltip-id="watchlist-groups-tooltip"
                    data-tooltip-content="Fetch the latest market data (premium, underlying, greeks) for all contracts in this group."
                  >
                    {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                  </button>
                  <button
                    onClick={() => handleRunGroup(group.id)}
                    disabled={isRefreshing}
                    className={`btn-primary text-xs ${
                      isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    data-tooltip-id="watchlist-groups-tooltip"
                    data-tooltip-content="Run a simulation on all contracts in this group to project P&L under multiple price scenarios. Results will appear in the watchlist table below."
                  >
                    Run Group
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group.id, group.name, group.contracts.length);
                    }}
                    disabled={isRefreshing || editingGroupId !== null}
                    className={`btn-ghost-danger ${
                      isRefreshing || editingGroupId !== null ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    data-tooltip-id="watchlist-groups-tooltip"
                    data-tooltip-content="Delete this group. Contracts are not deleted — they are only removed from this group."
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Progress indicator */}
              {isRefreshing && refreshJob && (
                <div className="mb-2 p-2 bg-blue-900/30 rounded border border-blue-500/30">
                  <div className="text-xs text-blue-300">
                    Refreshing {refreshJob.processed_contracts || 0} of {refreshJob.total_contracts || 0} contracts
                    {refreshJob.progress_percentage ? ` (${refreshJob.progress_percentage}%)` : ''}
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${refreshJob.progress_percentage || 0}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {!isCollapsed && (
                <>
                  {group.contracts.length > 0 ? (
                    <>
                      {/* Responsive Grid for Contracts */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {console.log(`📝 Group ${group.id} Contracts:`, group.contracts)}
                        {[...group.contracts]
                          .sort((a, b) => a.ticker.localeCompare(b.ticker))
                          .slice(0, expandedGroups[group.id] ? group.contracts.length : Math.min(group.contracts.length, 4))
                          .map((c) => (
                            <WatchlistContractCard
                              key={c.id}
                              contract={c}
                              isSelected={selectedContracts.includes(c.id)}
                              onSelect={(id, checked) => {
                                console.log(
                                  `☑ Contract ID ${id} was ${checked ? 'selected' : 'deselected'}.`
                                );
                                if (checked) {
                                  setSelectedContracts((prev) => [...prev, id]);
                                } else {
                                  setSelectedContracts((prev) => prev.filter((cid) => cid !== id));
                                }
                              }}
                              onReset={handleResetDays}
                              onRefresh={handleRefresh}
                              onUpdateGroups={(id) => openAssignModalForContracts([id])}
                              onDelete={(id) => handleDeleteContract(id, group.id)}
                              onContractUpdate={fetchGroups}
                              baselineMode={baselineMode}
                              setBaselineMode={setBaselineMode}
                            />
                          ))}
                      </div>

                      {/* See All Contracts Button (only show if >4 contracts) */}
                      {group.contracts.length > 4 && (
                        <div className="mt-4 flex justify-center">
                          <button
                            onClick={() => toggleShowAll(group.id)}
                            className="btn-secondary text-sm"
                          >
                            {expandedGroups[group.id]
                              ? `Show Less`
                              : `See All Contracts (${group.contracts.length})`}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-muted pl-1 italic">
                      (No contracts assigned)
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {selectedContracts.length > 0 && (
        <div className="flex gap-2 mt-4 flex-wrap">
          <button
            onClick={async () => {
              // Show loading modal with selected contract count
              handleSimulationStart(selectedContracts.length);
              try {
                await handleRunSelected();
              } catch (error) {
                console.error('Error running selected:', error);
                handleSimulationComplete([]);
              }
            }}
            className="btn-primary"
          >
            Run Selected ({selectedContracts.length})
          </button>
          <button
            onClick={async () => {
              if (!window.confirm(`Delete ${selectedContracts.length} selected contract(s)?`)) {
                return;
              }
              try {
                // Delete each selected contract
                for (const contractId of selectedContracts) {
                  await apiClient.delete(`/saved-contracts/${contractId}/`);
                }
                alert('Contracts deleted successfully!');
                setSelectedContracts([]);
                await fetchGroups();
              } catch (error) {
                console.error('❌ Error deleting contracts:', error);
                alert('Failed to delete contracts.');
              }
            }}
            className="btn-ghost-danger"
          >
            Delete Selected ({selectedContracts.length})
          </button>
          <button
            onClick={() => {
              console.log('Assigning selected contracts:', selectedContracts);
              openAssignModalForContracts(selectedContracts);
            }}
            className="btn-action-accent"
          >
            Assign Selected to Groups
          </button>
        </div>
      )}

      <WatchlistAssignGroupsModal
        isOpen={assignModalVisible}
        onClose={() => {
          console.log('❌ Closing Assign Modal');
          setAssignModalVisible(false);
          setContractDetailsForAssignment([]);
        }}
        groups={groups}
        fetchGroups={fetchGroups}
        contracts={contractsForAssignment}
        contractDetails={contractDetailsForAssignment}
        onAssign={async (selectedGroupIds, mode) => {
          console.group('📤 Assigning Contracts to Groups');
          console.log('Selected Groups:', selectedGroupIds);
          console.log('Contracts for Assignment:', contractsForAssignment);
          console.log('Mode:', mode);
          try {
            for (const groupId of selectedGroupIds) {
              console.log(`➡ Sending assign request to group ID: ${groupId}`);
              await apiClient.post(`/watchlist-groups/${groupId}/assign-contracts/`, {
                contract_ids: contractsForAssignment,
                mode,
              });
            }
            alert('Contracts assigned successfully!');
            setAssignModalVisible(false);
            setSelectedContracts([]); // Clear selection after assignment
            await fetchGroups();
          } catch (error) {
            console.error('❌ Error assigning contracts to groups:', error);
            alert(`Failed to assign contracts to groups: ${error.response?.data?.error || error.message}`);
          }
          console.groupEnd();
        }}
      />

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}

      {/* Group Button Tooltips */}
      <ReactTooltip
        id="watchlist-groups-tooltip"
        place="top"
        delayShow={300}
        delayHide={100}
        style={{
          backgroundColor: '#111827',
          color: '#ffffff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '13px',
          maxWidth: '280px',
          lineHeight: '1.4',
          zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151',
        }}
      />
    </div>
  );
}

export default WatchlistGroups;
